import type {ChangeEvent, JSX} from 'react';
import {Link} from 'react-router-dom';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Button,
  CheckBox,
  Input,
  Table,
  TBody,
  Td,
  TextArea,
  Th,
  Thead,
  Tr,
} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {BusinessTransactionStatus} from '@/types/AfriexBusinessTransactionStatus';
import * as formatDate from '@/utils/date';

import PoolDepositDuplicateBanner from './PoolDepositDuplicateBanner';
import {usePoolDepositReview} from './usePoolDepositReview';
import {usePoolReviewActors} from './usePoolReviewActors';

type PoolDepositReviewPanelProps = {
  data: AfriexBusinessTransaction;
};

/**
 * Review surface for a pool deposit the entity submitted itself with a proof of
 * payment. Renders for transactions carrying a poolReview block — normally
 * only entity submissions, but also an operator-typed confirm-deposit whose
 * duplicate guard was overridden (see `duplicateOverride` below).
 *
 * Once reviewed, the panel collapses to a read-only record of what happened.
 */
export default function PoolDepositReviewPanel({
  data,
}: PoolDepositReviewPanelProps): JSX.Element | null {
  const poolReview = data.meta?.poolReview;
  const submittedAmount = Number(data.destinationAmount?.value ?? 0);
  const isAwaitingReview =
    data.status === BusinessTransactionStatus.IN_REVIEW && !!poolReview;

  const {
    amount,
    setAmount,
    isAmountAdjusted,
    reason,
    setReason,
    resubmissionRequired,
    setResubmissionRequired,
    isRejecting,
    setIsRejecting,
    isLoading,
    canApprove,
    canReject,
    handleApprove,
    handleReject,
    duplicateWarning,
    isDuplicateDismissed,
    confirmApproveDuplicate,
    dismissDuplicateWarning,
  } = usePoolDepositReview(data.id, submittedAmount);

  const {resolveActor, businessName} = usePoolReviewActors(
    poolReview?.notes,
    data.businessId,
  );

  // A pool deposit with no proof-of-payment submission (the operator-typed
  // confirm-deposit path) still carries a `poolReview` block when its
  // duplicate guard was overridden — see BusinessPoolAccountInteractor. That
  // path's poolReview is exactly `{duplicateOverride}` and nothing else, so
  // any other key present means this is an entity submission (whichever
  // fields it happens to carry — individual ones can legitimately be absent
  // on older data, so no single field is a reliable signal on its own).
  if (!poolReview) {
    return null;
  }
  const isEntitySubmission = Object.keys(poolReview).some(
    (key) => key !== 'duplicateOverride',
  );
  if (!isEntitySubmission && !poolReview.duplicateOverride) {
    return null;
  }

  return (
    <section
      className="mt-8 border rounded-md p-4 dark:border-gray-700"
      data-cy="pool-review-panel"
    >
      {poolReview.duplicateOverride && (
        <div
          className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200"
          data-testid="pool-review-duplicate-override"
        >
          Duplicate guard overridden by {poolReview.duplicateOverride.by} on{' '}
          {formatDate.DDMonYYYY(poolReview.duplicateOverride.at, true)}, against
          transaction {poolReview.duplicateOverride.of}.
        </div>
      )}

      {!isEntitySubmission ? null : (
        <>
          <h3 className="font-semibold text-md mb-3">Proof of payment</h3>

          <dl className="grid grid-cols-2 gap-2 text-sm mb-4">
            <dt className="text-gray-500">Reference type</dt>
            <dd>{poolReview.referenceType ?? '-'}</dd>
            <dt className="text-gray-500">Entity</dt>
            <dd>{businessName || poolReview.submittedBy || '-'}</dd>
            <dt className="text-gray-500">Declared amount</dt>
            <dd>
              {submittedAmount} {data.destinationAmount?.currencyCode}
            </dd>
            {/* When the payer says the wire left their bank, not when we
                acted. Dashed when submitted before the field existed. */}
            <dt className="text-gray-500">Payment sent</dt>
            <dd data-testid="pool-review-payment-sent-at">
              {data.meta?.poolDeposit?.paymentSentAt
                ? formatDate.DDMonYYYY(
                    data.meta.poolDeposit.paymentSentAt,
                    true,
                  )
                : '-'}
            </dd>
          </dl>

          {data.signedProofOfPayment ? (
            <a
              href={data.signedProofOfPayment}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline text-sm"
              data-testid="pool-proof-link"
            >
              Open the uploaded proof
            </a>
          ) : (
            <p className="text-sm text-gray-500">
              The uploaded file could not be loaded.
            </p>
          )}
        </>
      )}

      {isEntitySubmission && isAwaitingReview ? (
        <PermissionsProvider
          permission={AfriexAdminPermission.BusinessPoolDepositReview}
        >
          <div className="mt-4 border-t pt-4 dark:border-gray-700">
            <p className="text-sm text-gray-500 mb-3">
              Confirm this inflow on the bank statement before approving.
            </p>

            {duplicateWarning && (
              <PoolDepositDuplicateBanner
                warning={duplicateWarning}
                isLoading={isLoading}
                onDismiss={dismissDuplicateWarning}
                onConfirm={confirmApproveDuplicate}
                confirmLabel="Approve anyway (separate inflow)"
                testIdPrefix="pool-review"
                isDismissed={isDuplicateDismissed}
              >
                A matching pool deposit already exists for this business,
                reference, and amount (transaction{' '}
                <Link
                  to={`/merchants/transactions/${duplicateWarning.existingTransactionId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                  data-testid="pool-review-duplicate-link"
                >
                  {duplicateWarning.existingTransactionId}
                </Link>
                , status {duplicateWarning.existingStatus}
                {duplicateWarning.existingSubmittedAt &&
                  `, submitted ${formatDate.DDMonYYYY(
                    duplicateWarning.existingSubmittedAt,
                    true,
                  )}`}
                ).{' '}
                {isDuplicateDismissed
                  ? 'Approve stays unavailable until the page is reloaded. You can still reject this deposit.'
                  : 'Also approving this deposit will credit the wallet a second time.'}
              </PoolDepositDuplicateBanner>
            )}

            {!isRejecting ? (
              <div className="flex flex-col gap-3">
                <Input
                  label="Amount to credit"
                  type="number"
                  min="0"
                  step="any"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="pool-review-amount"
                />
                {isAmountAdjusted && (
                  <p
                    className="text-xs text-amber-600"
                    data-cy="pool-review-amount-adjusted"
                  >
                    This differs from the {submittedAmount} the entity declared.
                    The adjustment will be recorded and shown in their email.
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="solid"
                    colorScheme="green"
                    onClick={() => handleApprove()}
                    // Disabled once a duplicate is flagged, even after Cancel
                    // (#7356): re-clicking, even with an edited amount, would
                    // re-hit the same 409. "Approve anyway" is the override.
                    disabled={!canApprove || !!duplicateWarning}
                    isLoading={isLoading}
                    loadingText="Approving…"
                    data-testid="pool-review-approve"
                  >
                    Approve and credit
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    colorScheme="red"
                    onClick={() => setIsRejecting(true)}
                    disabled={isLoading}
                    data-testid="pool-review-open-reject"
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <TextArea
                  label="Reason for rejection"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  aria-rowspan={3}
                  data-testid="pool-review-reason"
                />
                <CheckBox
                  label="Ask the entity to re-submit"
                  checked={resubmissionRequired}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setResubmissionRequired(e.target.checked)
                  }
                  data-testid="pool-review-resubmission"
                />
                <p className="text-xs text-gray-500">
                  Leave unchecked if you will confirm and enter this inflow
                  yourself.
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="solid"
                    colorScheme="red"
                    onClick={handleReject}
                    disabled={!canReject}
                    isLoading={isLoading}
                    loadingText="Rejecting…"
                    data-testid="pool-review-reject"
                  >
                    Confirm rejection
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    colorScheme="cyan"
                    onClick={() => setIsRejecting(false)}
                    disabled={isLoading}
                    data-cy="pool-review-reject-back"
                  >
                    Back
                  </Button>
                </div>
              </div>
            )}
          </div>
        </PermissionsProvider>
      ) : (
        <div className="mt-4 border-t pt-4 text-sm dark:border-gray-700">
          {poolReview.rejectionReason && (
            <p className="mb-2" data-cy="pool-review-rejection">
              <span className="text-gray-500">Rejected: </span>
              {poolReview.rejectionReason}
              {poolReview.resubmissionRequired
                ? ' (re-submission requested)'
                : ''}
            </p>
          )}
          {!!poolReview.notes?.length && (
            <div className="mt-3">
              <h4 className="font-semibold text-gray-900 dark:text-gray-300 mb-2">
                REVIEW ACTIVITY
              </h4>
              <div className="max-h-[300px] overflow-y-auto overflow-x-hidden">
                <Table>
                  <Thead>
                    <Tr enableHover={false}>
                      <Th className="px-3 py-2 text-left">Date</Th>
                      <Th className="px-3 py-2 text-left">Actor</Th>
                      <Th className="px-3 py-2 text-left">Action</Th>
                      <Th className="px-3 py-2 text-left">Note</Th>
                    </Tr>
                  </Thead>
                  <TBody>
                    {/* Newest first, matching the RFI audit log. */}
                    {[...poolReview.notes].reverse().map((note, index) => (
                      <Tr
                        key={`${note.action}-${index}`}
                        data-cy="pool-review-activity-row"
                        data-action={note.action}
                      >
                        <Td className="px-3 py-2">
                          {note.timestamp
                            ? formatDate.DDMonYYYY(String(note.timestamp), true)
                            : '-'}
                        </Td>
                        <Td className="px-3 py-2">
                          {resolveActor(note)} ({note.actorType})
                        </Td>
                        <Td className="px-3 py-2">{note.action}</Td>
                        <Td
                          className="px-3 py-2"
                          data-cy="pool-review-activity-note"
                        >
                          {note.note ?? '-'}
                        </Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
